import { api } from './client'

/**
 * F-062 (TASK-0316): client di `AutomationRulesController` (`/api/automation-rules`) — le regole
 * "evento di business + condizioni -> azioni interne" e il registro delle loro esecuzioni. Tutto
 * riservato all'Admin (il server risponde 403 agli altri). Una regola nasce in Bozza, e' attiva solo
 * dopo `setStatus('Attiva')`; una regola attiva non si modifica (409).
 */
/** Bozza/Attiva/Archiviata sono i comandi di sempre; gli altri sono gli stati del ciclo di vita uniforme (R2-REGOLE). */
export type AutomationRuleStatus =
  | 'Bozza' | 'Attiva' | 'Archiviata' | 'Test' | 'Simulazione' | 'Approvazione' | 'Pubblicata'
export type AutomationActionKind = 'CreaTask' | 'NotificaCrm' | 'ChiudiTaskPratica'
export type AutomationAssignTo = 'agente' | 'utente'
export type AutomationConditionOperator = 'eq' | 'ne' | 'gt' | 'lt' | 'contains'
export type AutomationRunOutcome = 'Riuscita' | 'Fallita' | 'NonApplicabile'

export interface AutomationCondition {
  field: string
  operator: AutomationConditionOperator
  value: string
}

export interface AutomationAction {
  kind: AutomationActionKind
  title?: string | null
  body?: string | null
  assignTo?: AutomationAssignTo | null
  assigneeUserId?: number | null
  dueInHours?: number | null
}

export interface AutomationRule {
  id: number
  name: string
  eventType: string
  status: AutomationRuleStatus
  conditions: AutomationCondition[]
  actions: AutomationAction[]
  createdBy: number | null
  createdAt: string
  updatedAt: string | null
}

export interface AutomationRuleBody {
  name: string
  eventType: string
  conditions: AutomationCondition[]
  actions: AutomationAction[]
}

export interface AutomationEventAttribute {
  name: string
  label: string
}

export interface AutomationEventType {
  code: string
  label: string
  entityType: string
  attributes: AutomationEventAttribute[]
}

export interface AutomationRun {
  id: number
  ruleId: number
  eventKey: string
  eventType: string
  entityId: number
  outcome: AutomationRunOutcome
  error: string | null
  actionsExecuted: number
  occurredAt: string
  executedAt: string
}

export const AUTOMATION_OPERATOR_LABELS: Record<AutomationConditionOperator, string> = {
  eq: 'e\' uguale a',
  ne: 'e\' diverso da',
  gt: 'maggiore di',
  lt: 'minore di',
  contains: 'contiene',
}

export const AUTOMATION_ACTION_LABELS: Record<AutomationActionKind, string> = {
  CreaTask: 'Crea un task',
  NotificaCrm: 'Notifica nel CRM',
  ChiudiTaskPratica: 'Chiudi i task automatici della pratica',
}

export const automationsApi = {
  eventTypes: () => api<AutomationEventType[]>('/api/automation-rules/event-types'),
  list: (status?: AutomationRuleStatus) =>
    api<AutomationRule[]>(`/api/automation-rules${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  create: (body: AutomationRuleBody) =>
    api<AutomationRule>('/api/automation-rules', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: AutomationRuleBody) =>
    api<AutomationRule>(`/api/automation-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  setStatus: (id: number, status: AutomationRuleStatus) =>
    api<AutomationRule>(`/api/automation-rules/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  remove: (id: number) => api<void>(`/api/automation-rules/${id}`, { method: 'DELETE' }),
  runs: (id: number, limit = 50) => api<AutomationRun[]>(`/api/automation-rules/${id}/runs?limit=${limit}`),
}
